<script setup lang="ts">
import {computed, type PropType, ref, watch} from 'vue';
import {ElDialog, ElMessage, ElSwitch} from 'element-plus';
import {type CabUser, type SessieInfo, type SessieTelling} from 'common';
import {USER_CHANGE_EMIT, USER_SESSIONS_ENDED_EMIT} from '@/base/cabConstants';
import {endSession, endSessions, sendPasswordMail, sessiesVan} from '@/base/callBackend';
import {renderDateTimeLocal} from '@/base/utils';
import CardPanel from '@/views/ui/CardPanel.vue';
import RowGrid from '@/views/ui/RowGrid.vue';
import IconButton from '@/views/ui/IconButton.vue';
import TextButton from '@/views/ui/TextButton.vue';
import StatusTag from '@/views/ui/StatusTag.vue';
import {LogOut, Mail} from 'lucide-vue-next';

const SESSIE_COLUMNS: string = '9rem 9rem 8rem minmax(10rem, 1fr) 8rem';

const visible = defineModel<boolean>({required: true});
const emits   = defineEmits([USER_CHANGE_EMIT, USER_SESSIONS_ENDED_EMIT]);
const props   = defineProps({
    user: {
        type    : Object as PropType<CabUser>,
        required: true,
    },
    telling: {
        type    : Object as PropType<SessieTelling>,
        required: false,
    },
});

const sessies     = ref<SessieInfo[]>([]);
const sendingMail = ref<boolean>(false);
const bezig       = ref<boolean>(false);

const laatsteLogin = computed<string>(() => props.user.lastLoginAt ? renderDateTimeLocal(props.user.lastLoginAt) : 'nog nooit');
const browsers     = computed<number>(() => props.telling?.browsers ?? 0);
const nietsTeDoen  = computed<boolean>(() => sessies.value.length === 0 && browsers.value === 0);

watch(visible, async (open: boolean) => {
    if (open) {
        await laadSessies();
    }
});

async function laadSessies() {
    if (!props.user.email) {
        sessies.value = [];
        return;
    }
    try {
        sessies.value = await sessiesVan(props.user.email);
    } catch (error: any) {
        ElMessage.error(`Sessies van ${props.user.email} niet geladen`);
    }
}

function onTweeFactor() {
    emits(USER_CHANGE_EMIT, props.user);
}

async function onSendPasswordMail() {
    sendingMail.value = true;
    try {
        await sendPasswordMail(props.user.email!);
        ElMessage.success(`Mail verstuurd naar ${props.user.email}`);
    } catch (error: any) {
        ElMessage.error(`Mail niet verstuurd naar ${props.user.email}`);
    } finally {
        sendingMail.value = false;
    }
}

async function onEndSession(sessie: SessieInfo) {
    await beeindig(() => endSession(props.user.email!, sessie.id), 'Sessie beeindigd', 'Sessie niet beeindigd');
}

async function onEndSessions() {
    await beeindig(() => endSessions(props.user.email!), `Sessies beeindigd van ${props.user.email}`, `Sessies niet beeindigd van ${props.user.email}`);
}

async function beeindig(actie: () => Promise<void>, gelukt: string, mislukt: string) {
    bezig.value = true;
    try {
        await actie();
        ElMessage.success(gelukt);
        await laadSessies();
        emits(USER_SESSIONS_ENDED_EMIT);
    } catch (error: any) {
        ElMessage.error(mislukt);
    } finally {
        bezig.value = false;
    }
}
</script>

<template>
    <el-dialog
        v-model="visible"
        :title="user.name"
        width="820px"
        :close-on-press-escape="true"
        :close-on-click-modal="true"
        data-testid="user-auth-dialog"
    >
        <div class="text-xs text-zzc-muted -mt-2 mb-4">{{ user.email }}</div>
        <div class="flex flex-col gap-3 text-sm">
            <!-- staff always log in with a mailed code and have no password -->
            <div v-if="!user.medewerker" class="flex flex-row items-center gap-3">
                <span class="w-44 text-zzc-muted">Inlogcode per mail (2FA)</span>
                <el-switch v-model="user.tweeFactor" data-testid="two-factor-switch" @change="onTweeFactor"/>
            </div>
            <div class="flex flex-row items-center gap-3">
                <span class="w-44 text-zzc-muted">Laatst ingelogd</span>
                <span data-testid="last-login">{{ laatsteLogin }}</span>
            </div>
            <div v-if="!user.medewerker" class="flex flex-row items-center gap-3">
                <span class="w-44 text-zzc-muted">Wachtwoord</span>
                <TextButton
                    :icon="Mail"
                    :disabled="!user.email"
                    :loading="sendingMail"
                    title="mail versturen waarmee de gebruiker zelf een wachtwoord kan instellen"
                    data-testid="send-password-mail-button"
                    @click="onSendPasswordMail"
                >
                    Wachtwoordmail sturen
                </TextButton>
            </div>
            <CardPanel title="Sessies" :count="`${browsers} vertrouwde browser${browsers === 1 ? '' : 's'}`" class="mt-2">
                <template #actions>
                    <TextButton
                        variant="danger"
                        :icon="LogOut"
                        :disabled="!user.email || nietsTeDoen"
                        :loading="bezig"
                        title="alle sessies beeindigen en alle vertrouwde browsers vergeten"
                        data-testid="end-sessions-button"
                        @click="onEndSessions"
                    >
                        Alle sessies beeindigen
                    </TextButton>
                </template>
                <div v-if="sessies.length === 0" class="px-4 py-3 text-xs text-zzc-muted">
                    geen actieve sessies
                </div>
                <template v-else>
                    <RowGrid :columns="SESSIE_COLUMNS" header>
                        <span>aangemaakt</span>
                        <span>laatst gezien</span>
                        <span>ip</span>
                        <span>browser</span>
                        <span/>
                    </RowGrid>
                    <RowGrid v-for="sessie in sessies" :key="sessie.id" :columns="SESSIE_COLUMNS" data-testid="sessie-regel">
                        <span class="whitespace-nowrap">{{ renderDateTimeLocal(new Date(sessie.aangemaaktOp)) }}</span>
                        <span class="whitespace-nowrap">{{ renderDateTimeLocal(new Date(sessie.laatstGezienOp)) }}</span>
                        <span class="whitespace-nowrap">{{ sessie.ip }}</span>
                        <span class="truncate" :title="sessie.browser">{{ sessie.browser }}</span>
                        <span class="flex items-center justify-end gap-2">
                            <StatusTag v-if="sessie.huidig" kind="info">deze sessie</StatusTag>
                            <IconButton :icon="LogOut" label="sessie beeindigen" variant="danger" size="sm" :loading="bezig" @click="onEndSession(sessie)"/>
                        </span>
                    </RowGrid>
                </template>
            </CardPanel>
        </div>
    </el-dialog>
</template>
